import React, { useEffect, useMemo, useState } from "react";

import { useHotel } from "@/hooks/useHotel";

import {
  useStripeConnectOnboardingStatus,
  useCreateStripeConnect,
  useHotelPaymentMethods,
  useSyncStripeAccountStatus,
  useRefreshStripeConnect,
  useUpdateHotelPaymentMethod,
  useUpdateHotelPaymentSettings,
} from "./hooks/billing-settings.hooks";
import { useTranslation } from "react-i18next";
import { SavingHint } from "./components/SavingHint";
import { BillingCard } from "./components/BillingCard";
import { CurrencyRow } from "./components/CurrencyRow";
import { StripeAction } from "./components/StripeAction";
import { StripeProblems } from "./components/StripeProblems";
import { StripeStatusRow } from "./components/StripeStatusRow";
import { BillingPageState } from "./components/BillingPageState";
import { BillingEnabledRow } from "./components/BillingEnableRow";
import { BooleanIndicators } from "./components/BooleanIndicators";
import { ConnectStripeSection } from "./components/ConnectionStripeSection";
import { PaymentMethodsSection } from "./components/PaymentMethodsSection";
import { buildUrl, goTo, needsStripeAction, normalizeCurrency } from "./utils";
import {
  HotelPaymentMethod,
  PaymentMethodPreference,
  StripeAccountRequirementsState,
} from "@/services/interfaces/IBillingSettingsService";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { BillingSettingsNavigation } from "./components/BillingSettingsNavigation";

interface BillingSettingsLayoutProps {
  children: React.ReactNode;
}

const BillingSettingsLayout: React.FC<BillingSettingsLayoutProps> = ({
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mx-auto flex w-full max-w-[1344px] flex-col gap-6 p-6 md:p-8">
      <BillingSettingsNavigation activeTab="settings" />
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-black/96">
          {t("billing.settingsTitle")}
        </h1>
        <p className="max-w-3xl text-sm text-black/65">
          {t("billing.settingsDescription")}
        </p>
      </header>
      {children}
    </div>
  );
};

export const BillingSettingsPage = (): React.JSX.Element => {
  const { hotelId } = useHotel();
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();
  const canManageBilling = capabilities.canManageBilling;

  const syncStripe = useSyncStripeAccountStatus();
  const { data, isLoading, isError } = useStripeConnectOnboardingStatus();
  const stripeStatus = data as StripeAccountRequirementsState | undefined;
  const paymentsEnabled = Boolean(stripeStatus?.paymentEnabled);
  const paymentMethods = useHotelPaymentMethods(paymentsEnabled);

  const createStripe = useCreateStripeConnect();
  const refreshStripe = useRefreshStripeConnect();
  const updatePaymentSettings = useUpdateHotelPaymentSettings();
  const updatePaymentMethod = useUpdateHotelPaymentMethod();

  const [selectedCountry, setSelectedCountry] = useState<string>("US");

  // Sync Stripe account status whenever hotel changes (guarded).
  useEffect(() => {
    if (!hotelId) return;
    if (!canManageBilling) return;
    syncStripe.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageBilling, hotelId]);

  const isSyncing = syncStripe.isPending;
  const isSaving = updatePaymentSettings.isPending;
  const isCreating = createStripe.isPending;
  const isRefreshing = refreshStripe.isPending;
  const isUpdatingMethod = updatePaymentMethod.isPending;

  const busy = isSyncing || isSaving || isCreating || isRefreshing;

  const currency = useMemo(
    () => normalizeCurrency(stripeStatus?.currency),
    [stripeStatus?.currency],
  );

  const paymentMethodUpdateError = useMemo(() => {
    const error = updatePaymentMethod.error;

    if (!error || typeof error !== "object" || !("message" in error)) {
      return undefined;
    }

    return String(error.message);
  }, [updatePaymentMethod.error]);

  const handlePaymentMethodPreferenceChange = (
    method: HotelPaymentMethod,
    preference: Exclude<PaymentMethodPreference, null>,
  ) => {
    if (!canManageBilling) {
      return;
    }

    updatePaymentMethod.mutate({
      method: method.code,
      enabled: preference === "on",
      preference,
    });
  };

  if (!hotelId) {
    return (
      <BillingSettingsLayout>
        <BillingPageState text={t("billing.hotelNotSelected")} />
      </BillingSettingsLayout>
    );
  }

  if (isLoading || isSyncing) {
    return (
      <BillingSettingsLayout>
        <BillingPageState text={t("billing.loadingBillingStatus")} />
      </BillingSettingsLayout>
    );
  }

  if (isError) {
    return (
      <BillingSettingsLayout>
        <BillingPageState
          text={t("billing.failedToLoadBillingStatus")}
          tone="error"
        />
      </BillingSettingsLayout>
    );
  }

  // Not connected yet (no stripeStatus)
  if (!stripeStatus) {
    return (
      <BillingSettingsLayout>
        <BillingCard>
          <ConnectStripeSection
            selectedCountry={selectedCountry}
            onSelectCountry={setSelectedCountry}
            loading={isCreating}
            disabled={!canManageBilling}
            onConnect={() => {
              if (!canManageBilling) {
                return;
              }

              createStripe.mutate(
                {
                  businessType: "company",
                  country: selectedCountry,
                  refreshUrl: buildUrl("/billing-settings/refresh"),
                  returnUrl: buildUrl("/billing-settings"),
                },
                {
                  onSuccess: (res: any) => goTo(res.onboardingUrl),
                },
              );
            }}
          />
        </BillingCard>
      </BillingSettingsLayout>
    );
  }

  return (
    <BillingSettingsLayout>
      <BillingCard>
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-black/96">
            {t("billing.configurationTitle")}
          </h2>
          <p className="text-sm text-black/65">
            {t("billing.configurationDescription")}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <BillingEnabledRow
            checked={Boolean(stripeStatus.paymentEnabled)}
            disabled={!canManageBilling || busy}
            onChange={(checked) => {
              if (!canManageBilling) {
                return;
              }

              updatePaymentSettings.mutate({
                paymentEnabled: checked,
                currency,
              });
            }}
          />

          <CurrencyRow
            value={currency}
            disabled={!canManageBilling || busy}
            onChange={(nextCurrency) => {
              if (!canManageBilling) {
                return;
              }

              updatePaymentSettings.mutate({
                paymentEnabled: Boolean(stripeStatus.paymentEnabled),
                currency: normalizeCurrency(nextCurrency),
              });
            }}
          />
        </div>

        <StripeStatusRow status={stripeStatus.status} />

        <BooleanIndicators
          items={[
            {
              label: t("billing.onboardingComplete"),
              ok: stripeStatus.onboardingComplete,
            },
            {
              label: t("billing.chargesEnabled"),
              ok: stripeStatus.chargesEnabled,
            },
            {
              label: t("billing.payoutsEnabled"),
              ok: stripeStatus.payoutsEnabled,
            },
          ]}
        />

        <StripeProblems
          disabledReason={stripeStatus.disabledReason}
          requirementErrors={stripeStatus.requirementErrors ?? undefined}
        />

        <StripeAction
          show={needsStripeAction(stripeStatus)}
          loading={isRefreshing}
          disabled={!canManageBilling || busy}
          onClick={() => {
            if (!canManageBilling) {
              return;
            }

            refreshStripe.mutate(
              {
                refreshUrl: buildUrl("/billing-settings/refresh"),
                returnUrl: buildUrl("/billing-settings"),
              },
              {
                onSuccess: (res: any) => goTo(res.onboardingUrl),
              },
            );
          }}
        />

        <SavingHint show={isSaving} />
      </BillingCard>

      {paymentsEnabled ? (
        <BillingCard>
          <PaymentMethodsSection
            data={paymentMethods.data}
            isLoading={paymentMethods.isLoading}
            isError={paymentMethods.isError}
            isUpdating={isUpdatingMethod}
            disabled={!canManageBilling}
            updateError={paymentMethodUpdateError}
            onRetry={() => paymentMethods.refetch()}
            onPreferenceChange={handlePaymentMethodPreferenceChange}
          />
        </BillingCard>
      ) : null}
    </BillingSettingsLayout>
  );
};
